import { useMemo, ReactNode } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Components } from "react-markdown";
import type { AssistantMarkdownImage } from "../../types/assistant";
import type { Photo, Video } from "../../types";
import { PhotoWidget } from "../PhotoWidget";
import styles from "./MarkdownBlock.module.css";

const remarkPlugins = [remarkGfm];

type MarkdownBlockProps = {
  content: string;
  images: AssistantMarkdownImage[];
  onPhotosClick: (items: Array<Photo | Video>) => void;
};

const IMAGE_LINE_RE = /^!\[([^\]]*)\]\(([^)]+)\)\s*$/;

type TextSegment = { type: "text"; content: string };
type ImageGroupSegment = { type: "images"; srcs: string[] };
type ContentSegment = TextSegment | ImageGroupSegment;

const splitContentSegments = (content: string): ContentSegment[] => {
  const lines = content.split("\n");
  const segments: ContentSegment[] = [];
  let textLines: string[] = [];

  const flushText = () => {
    if (textLines.length > 0) {
      const text = textLines.join("\n").trim();
      if (text) {
        segments.push({ type: "text", content: text });
      }
      textLines = [];
    }
  };

  for (const line of lines) {
    const match = line.match(IMAGE_LINE_RE);
    if (match) {
      const src = match[2];
      const last = segments[segments.length - 1];
      if (textLines.length === 0 && last?.type === "images") {
        last.srcs.push(src);
      } else {
        flushText();
        segments.push({ type: "images", srcs: [src] });
      }
    } else {
      textLines.push(line);
    }
  }

  flushText();
  return segments;
};

const toPhoto = (image: AssistantMarkdownImage): Photo => ({
  type: "photo",
  id: image.id,
  src: image.src,
  width: image.width,
  height: image.height,
  caption: image.caption ?? undefined,
  thumbnailSrc: image.thumbnailSrc ?? undefined,
});

export const MarkdownBlock = ({
  content,
  images,
  onPhotosClick,
}: MarkdownBlockProps) => {
  const imageMap = useMemo(
    () => new Map(images.map((img) => [img.src, img])),
    [images],
  );

  const components = useMemo((): Components => ({
    a: ({ href, children }) => (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    ),
  }), []);

  const segments = useMemo(() => splitContentSegments(content), [content]);

  const nodes = useMemo((): ReactNode[] =>
    segments.map((segment, i) => {
      if (segment.type === "images") {
        const photos = segment.srcs
          .map((src) => imageMap.get(src))
          .filter((meta): meta is AssistantMarkdownImage => Boolean(meta))
          .map(toPhoto);

        if (photos.length === 0) {
          return null;
        }

        return (
          <PhotoWidget
            key={`gallery-${i}`}
            items={photos}
            onClick={(items) => onPhotosClick(items)}
            className={styles.image}
          />
        );
      }

      return (
        <Markdown key={`text-${i}`} remarkPlugins={remarkPlugins} components={components}>
          {segment.content}
        </Markdown>
      );
    }),
    [segments, imageMap, components, onPhotosClick],
  );

  return <div className={styles.markdown}>{nodes}</div>;
};
